// ─────────────────────────────────────────────────────────────────────────────
// RexyOrbThree — WebGL particle orb for the desktop homepage centre (Chunk 5).
// Three.js port of mobile/rexy-ios/src/components/home/RexyOrb.tsx (Skia).
//
// PHYSICS PORTED 1:1 (CPU sim, deterministic — no Math.random in the loop):
//   • N=4096 particles, 8 floats each [x y z vx vy vz age seed] in unit-sphere space
//   • unit-sphere birth; particles sit ~stuck for 80–100 frames, then drift
//   • noise jitter (RANDOM_ACCEL) + DAMPING; SHELL_STRENGTH pulls back to the surface
//   • attack/hold/decay alpha envelope = 50/50/150 (TOTAL 250); recycle to a fresh
//     birth point on death
//   • audio drives ONLY a random 3–10% of individual particles (pop outward along
//     their own normal). The sphere itself NEVER scales with voice — calm Y rotation.
//
// RENDERING (WebGL-native equivalent of the Skia draw):
//   • Points + BufferGeometry; per-frame CPU sim writes the position + alpha buffers.
//   • Custom ShaderMaterial: teal→gold by uWarmth, soft halo via gl_PointCoord,
//     depth-fade on the far side, additive blending for the glow.
//   • Core = a SEPARATE quad (not a particle): 5-stop radial gradient (cool→warm by
//     uWarmth), independent 2s pulse (radius + glow). A second small "hot core" quad
//     sits in front. Both gated on sessionActive (mirrors the iOS sessionF passes).
//
// The mic is opened once in RexyHomepage; userAmplitude/agentAmplitude/warmth arrive
// as plain props and are mirrored into refs each render so the rAF loop reads the
// latest without re-subscribing (the desktop equivalent of iOS SharedValues).
// Full GPU teardown on unmount: geometry, materials, renderer, and GL context.
//
// WEBGL AVAILABILITY GUARD (added):
//   • Before constructing THREE.WebGLRenderer we probe getContext('webgl'/'webgl2')
//     and wrap the renderer creation in try/catch. If either step fails the orb
//     falls back to a static SVG that mimics the diffuse core gradient + a few
//     pin lights. The app loads, sign-in works, and the mic trace can run even on
//     machines where WebGL is unavailable or its context returns null. Console
//     warns once so the failure isn't silent.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

// S1 — the traced silhouette target set. Numbers only; no image is ever loaded.
import {
  REXY_SILHOUETTE_COUNT,
  REXY_PRIMARY_COUNT,
  REXY_EYE_ANCHOR,
  REXY_SILHOUETTE_POSITIONS,
  REXY_SILHOUETTE_NORMALS,
  REXY_WAVE_COORDINATES,
  REXY_RELEASE_DELAYS,
} from './rexySilhouetteTargets';

// ── Constants ────────────────────────────────────────────────────────────────
const N = 4096; // S1.1: owner-ruled density lift 1880 -> 4096 (2.18x). The ONLY
                // accepted value authorised to change; every render constant below
                // is untouched and no compensation was applied.
const STRIDE = 8;
const ROT_SPEED = 0.32; // rad/s — calm, NOT audio-driven (D2: ~36% slower, premium drift)
const ATTACK = 50;
const HOLD = 50;
const DECAY = 150;
const TOTAL = ATTACK + HOLD + DECAY; // 250
const RANDOM_ACCEL = 0.003;
const SHELL_STRENGTH = 0.007;
const DAMPING = 0.975;

// Particle colour anchors (0-255), lerped by warmth — same as iOS STAR_COOL/GOLD.
const STAR_COOL: [number, number, number] = [80, 230, 240]; // D1: teal/cyan, not green
const STAR_GOLD: [number, number, number] = [255, 200, 80];

// Core gradient stops [r,g,b,a] — one seamless gradient, no rings (iOS Task 5).
const CORE_POS = [0, 0.05, 0.15, 0.35, 1.0];
const CORE_COOL: [number, number, number, number][] = [
  [250, 255, 255, 0.92],
  [140, 245, 255, 0.52],
  [0, 205, 255, 0.22],
  [0, 160, 255, 0.075],
  [0, 160, 255, 0.0],
];
const CORE_WARM: [number, number, number, number][] = [
  [255, 255, 235, 0.92],
  [255, 225, 100, 0.55],
  [255, 185, 35, 0.24],
  [255, 135, 20, 0.075],
  [255, 140, 20, 0.0],
];

// Camera distance — chosen so a unit-radius sphere fills ~0.3 of the frame
// (matching the iOS sphereRad = size*0.3). Constant regardless of pixel size.
const CAM_Z = 3.5;

// Desktop idle-state lock (differs from iOS, where the core only appears on session
// start): the orb is ALWAYS visible and alive. idleDim smoothly lerps the overall
// particle alpha + core intensity between IDLE_DIM (no session) and 1.0 (active) over
// ~1s — a smooth fade up/down, never a pop in or out.
const IDLE_DIM = 0.58; // D1 0.25→0.5; D2 0.5→0.58 (slightly brighter idle)
const DIM_LERP = 0.06; // per-frame @60fps → ~1s settle
// Warmth ease toward the AppShell-supplied target (Phase 1.2). Exp-lerp, same
// idiom as DIM_LERP; ~400ms settle @60fps (0.0245^(1/24) ≈ 0.857 → k ≈ 0.14),
// the GPU-loop equivalent of iOS withTiming({ duration: 400 }). Ghost-countersigned.
const WARMTH_LERP = 0.14;

// ── S1.2 silhouette + transition — MOVEMENT-ONLY constants ──────────────────
// These govern the Rexy-idle formation and the transition between formations.
// They do not replace, alter or shadow ANY accepted orb value: SHELL_STRENGTH,
// DAMPING, RANDOM_ACCEL, ROT_SPEED, IDLE_DIM, the lifecycle and every shader
// number above are untouched and still govern the call orb exactly as before.
const TAU = Math.PI * 2;
const SILHOUETTE_SPRING = 0.018;
const SILHOUETTE_DAMPING = 0.9;

// The Mexican wave. ONE crest, travelling back-of-skull -> snout, then a short
// rest with the whole head settled before the next crest enters behind the
// skull. Deliberately NOT a wrapped phase: wrapping puts a second crest at the
// snout while the first is still at the skull, which is the distributed shimmer
// this replaces.
const MEXICAN_WAVE_TRAVEL_SECONDS = 7.2;
const MEXICAN_WAVE_REST_SECONDS = 1.2;
const MEXICAN_WAVE_HALF_WIDTH = 0.085;
const MEXICAN_WAVE_AMPLITUDE = 0.017;
const MEXICAN_WAVE_SHARPNESS = 1.65;
const MEXICAN_WAVE_MARGIN = 0.1;

// Transition (S2). S2.1: ONE shared pace, both directions in step — between
// the old ~2.1s result and the 7.2s Mexican-wave register.
const FORMATION_TRANSITION_DURATION_MS = 2600;
const TO_ORB_DURATION_MS = FORMATION_TRANSITION_DURATION_MS;
const TO_SILHOUETTE_DURATION_MS = FORMATION_TRANSITION_DURATION_MS;
const TRANSITION_MAX_STAGGER = 0.26;
const REDUCED_TRANSITION_DURATION_MS = 450;
const REDUCED_SCATTER_SCALE = 0.15;
const REDUCED_MAX_STAGGER = 0.05;
const SCATTER_MIN = 0.085;
const SCATTER_MAX = 0.155;
const SCATTER_CURL = 0.045;

// S2.1 PART 1B — internal continuity timer inside orb-active. NOT a fifth
// formation phase. The accepted forces assume control over this window instead
// of arriving whole on the first active frame.
const ORB_ENTRY_BLEND_MS = 600;

// S2.1 PART 3 — the ONLY pace lever. A uniform rescale of the active-orb
// particle integrator: every force is multiplied by it and DAMPING is raised to
// it. The position step then integrates the already-scaled velocity, which
// measures at the ruled -10%. (Scaling the position step a SECOND time as well
// compounds to -19.4% — effectively 0.80, which the owner excluded.) RANDOM_ACCEL, SHELL_STRENGTH, DAMPING, the impulse and
// reactProbability all keep their exact accepted values — none is edited. It
// touches nothing else: not rotation, not the core, not warmth, not dim, not
// lifecycle age, not the silhouette, not the Mexican wave, not the transitions.
const ORB_PARTICLE_TIME_SCALE = 0.9;

// ── Scalar easing helpers (no allocation, safe in the frame loop) ────────────
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function smoothstep(edge0: number, edge1: number, x: number): number {
  const u = clamp01((x - edge0) / (edge1 - edge0 || 1e-6));
  return u * u * (3 - 2 * u);
}
function smootherstep(x: number): number {
  const u = clamp01(x);
  return u * u * u * (u * (u * 6 - 15) + 10);
}
function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

// The silhouette pool must be the same pool. If the generated set ever drifts
// from N the branch is clamped and says so — it never silently draws a subset.
if (REXY_SILHOUETTE_COUNT !== N) {
  // eslint-disable-next-line no-console
  console.warn(
    `[RexyOrbThree] silhouette target count ${REXY_SILHOUETTE_COUNT} !== N ${N}. ` +
      'Re-run scripts/generate_rexy_silhouette_targets.py.',
  );
}
const SIL_N = Math.min(N, REXY_SILHOUETTE_COUNT);

// REXY_RELEASE_DELAYS is generated in 0..MAX_STAGGER (measured 0.0254..0.2944),
// NOT 0..1. Multiplying it straight by TRANSITION_MAX_STAGGER would collapse
// the stagger to ~0.077 of the transition instead of 0.26. Normalise ONCE here
// — the generated data itself is untouched.
const RELEASE_DELAY_MAX = (() => {
  let m = 0;
  for (let i = 0; i < REXY_RELEASE_DELAYS.length; i++) {
    if (REXY_RELEASE_DELAYS[i] > m) m = REXY_RELEASE_DELAYS[i];
  }
  return m || 1;
})();
const RELEASE_DELAY_01 = (() => {
  const a = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    a[i] = i < REXY_RELEASE_DELAYS.length
      ? clamp01(REXY_RELEASE_DELAYS[i] / RELEASE_DELAY_MAX)
      : 1;
  }
  return a;
})();

type RexyFormationPhase = 'silhouette-idle' | 'to-orb' | 'orb-active' | 'to-silhouette';

// ── DEV-GATED TRANSITION TRACE (S2.1 diagnostics) ────────────────────────────
// Off unless window.__REXY_TRACE_TRANSITION__ === true. One pre-allocated
// Float64Array ring; NO strings, NO objects, NO console inside the RAF step —
// console output inside rAF is itself a stutter source.
const TRACE_FIELDS = 29;
const TRACE_CAP = 1800; // 30s @60fps
const PHASE_CODE: Record<RexyFormationPhase, number> = {
  'silhouette-idle': 0,
  'to-orb': 1,
  'orb-active': 2,
  'to-silhouette': 3,
};
function traceEnabled(): boolean {
  return typeof window !== 'undefined' && (window as any).__REXY_TRACE_TRANSITION__ === true;
}

// ── Deterministic pseudo-random (identical to iOS) ───────────────────────────
function noise(s: number, t: number): number {
  return Math.sin(s * 127.1 + t * 311.7) * Math.sin(s * 269.5 + t * 183.3);
}
function hash01(s: number): number {
  const x = Math.sin(s * 91.7) * 43758.5453;
  return x - Math.floor(x);
}

// Initial pool — Math.random allowed here (JS thread, once at mount).
function makeInitialState(): Float32Array {
  const arr = new Float32Array(N * STRIDE);
  for (let i = 0; i < N; i++) {
    const o = i * STRIDE;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI;
    const sx = Math.sin(phi) * Math.cos(theta);
    const sy = Math.cos(phi);
    const sz = Math.sin(phi) * Math.sin(theta);
    arr[o] = sx;
    arr[o + 1] = sy;
    arr[o + 2] = sz;
    arr[o + 3] = sx * 0.002;
    arr[o + 4] = sy * 0.002;
    arr[o + 5] = sz * 0.002;
    arr[o + 6] = Math.random() * TOTAL;
    arr[o + 7] = Math.random() * 100;
  }
  return arr;
}

// ── WebGL availability probe (NEW) ───────────────────────────────────────────
// Returns true if the browser can hand us a real WebGL context off a throwaway
// canvas. Runs ONCE per mount before we ever touch THREE.WebGLRenderer, so we
// can swap to the static fallback before the renderer constructor would crash.
function probeWebGL(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  try {
    const probeCanvas = document.createElement('canvas');
    const gl =
      probeCanvas.getContext('webgl2') ||
      probeCanvas.getContext('webgl') ||
      probeCanvas.getContext('experimental-webgl');
    if (!gl) return false;
    // Some environments return a context object but with no usable parameters;
    // calling getParameter on a dud context is what THREE's getMaxPrecision
    // crashes on, so reproduce that probe explicitly here.
    const param = (gl as WebGLRenderingContext).getParameter?.(
      (gl as WebGLRenderingContext).VERSION,
    );
    // Release the probe's context BEFORE returning. Browsers cap the number of
    // live WebGL contexts and evict the OLDEST when the cap is hit — so a probe
    // that is never released eventually kills a REAL renderer's context, which
    // then paints a stale frame and reads as a lower-resolution orb. Every mount
    // ran this probe, so repeated mounts degraded whichever orb was oldest.
    (gl as WebGLRenderingContext).getExtension?.('WEBGL_lose_context')?.loseContext?.();
    return typeof param === 'string' && param.length > 0;
  } catch {
    return false;
  }
}

// ── Shaders ───────────────────────────────────────────────────────────────────
const PARTICLE_VERT = `
attribute float aAlpha;
uniform float uPointBasePx;
uniform float uPixelRatio;
uniform float uCamZ;
varying float vAlpha;
varying float vDepthFade;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  // Tight crisp pins — perspective-scaled then HARD-clamped to a few physical px.
  float psize = uPointBasePx * (uCamZ / max(-mv.z, 0.001));
  gl_PointSize = clamp(psize * uPixelRatio, 2.0, 9.0);
  vAlpha = aAlpha;
  // Floored depth fade — far side dimmer, never invisible (hollow shell must read).
  float f = clamp((mv.z + (uCamZ + 1.0)) / 2.0, 0.0, 1.0);
  vDepthFade = mix(0.34, 1.0, pow(f, 1.25));
}`;

const PARTICLE_FRAG = `
precision mediump float;
uniform vec3 uStarCool;
uniform vec3 uStarGold;
uniform float uWarmth;
uniform float uDim;
varying float vAlpha;
varying float vDepthFade;
void main() {
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  float d = length(uv);
  if (d > 1.0) discard;

  // Pin/mid/halo split — the pin dominates; the halo barely whispers (no fog).
  float pin  = 1.0 - smoothstep(0.04, 0.30, d);
  float mid  = 1.0 - smoothstep(0.12, 0.62, d);
  float halo = 1.0 - smoothstep(0.35, 1.0, d);

  // Lifecycle envelope folded with floored depth fade + idle/active dim.
  float a = clamp(vAlpha * vDepthFade * uDim, 0.0, 1.0);

  float finalAlpha = pin * a * 1.00 + mid * a * 0.22 + halo * a * 0.06;
  if (finalAlpha < 0.002) discard;

  // White-hot pin centre so particles read as light sources, not flat dots.
  vec3 baseCol = mix(uStarCool, uStarGold, uWarmth);
  vec3 hotColor = mix(baseCol, vec3(1.0), pin * 0.28);
  vec3 finalColor = mix(baseCol, hotColor, pin);

  gl_FragColor = vec4(finalColor, finalAlpha);
}`;

const CORE_VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// Single 5-stop radial-gradient shader, reused for both the diffuse core and the hot
// core (the hot core bakes its per-stop alpha multipliers into uCoolA/uWarmA and keeps
// uGlowMul=1; the diffuse core animates uGlowMul on stops 1..4, stop 0 stays at 1).
const CORE_FRAG = `
precision mediump float;
uniform vec3 uCoolRGB[5];
uniform vec3 uWarmRGB[5];
uniform float uCoolA[5];
uniform float uWarmA[5];
uniform float uPos[5];
uniform float uWarmth;
uniform float uGlowMul;
uniform float uDim;
uniform float uCoreOpacity;
varying vec2 vUv;
void main() {
  float d = clamp(length(vUv - 0.5) * 2.0, 0.0, 1.0);
  vec3 col = mix(uCoolRGB[0], uWarmRGB[0], uWarmth);
  float a = 0.0;
  for (int i = 0; i < 4; i++) {
    if (d >= uPos[i] && d <= uPos[i + 1]) {
      float seg = max(uPos[i + 1] - uPos[i], 1e-4);
      float k = (d - uPos[i]) / seg;
      vec3 c0 = mix(uCoolRGB[i], uWarmRGB[i], uWarmth);
      vec3 c1 = mix(uCoolRGB[i + 1], uWarmRGB[i + 1], uWarmth);
      float a0 = mix(uCoolA[i], uWarmA[i], uWarmth) * (i == 0 ? 1.0 : uGlowMul);
      float a1 = mix(uCoolA[i + 1], uWarmA[i + 1], uWarmth) * uGlowMul;
      col = mix(c0, c1, k);
      a = mix(a0, a1, k);
    }
  }
  a *= uDim * uCoreOpacity;
  if (a <= 0.0) discard;
  gl_FragColor = vec4(col, a);
}`;

// Build the uniform set for a core material from cool/warm [r,g,b,a] stops (rgb /255).
function coreUniforms(
  cool: [number, number, number, number][],
  warm: [number, number, number, number][],
  pos: number[],
  opacity = 1,
) {
  return {
    uCoolRGB: { value: cool.map((c) => new THREE.Vector3(c[0] / 255, c[1] / 255, c[2] / 255)) },
    uWarmRGB: { value: warm.map((c) => new THREE.Vector3(c[0] / 255, c[1] / 255, c[2] / 255)) },
    uCoolA: { value: cool.map((c) => c[3]) },
    uWarmA: { value: warm.map((c) => c[3]) },
    uPos: { value: pos.slice() },
    uWarmth: { value: 0 },
    uGlowMul: { value: 1 },
    uDim: { value: IDLE_DIM },
    uCoreOpacity: { value: opacity },
  };
}

export interface RexyOrbThreeProps {
  size: number; // orb box edge in CSS px
  userAmplitude: number; // 0..1 (mic RMS)
  agentAmplitude: number; // 0..1
  warmth: number; // 0..1, teal→gold
  sessionActive: boolean;

  // ── S1, both OPTIONAL with safe defaults ───────────────────────────────────
  // Every existing mount passes neither and is byte-for-byte unaffected: the
  // defaults below reproduce the accepted behaviour exactly.
  /** 'sphere' = the accepted orb, always. 'rexy' = idle as the traced head. */
  idleFormation?: 'sphere' | 'rexy';
  /** 'fixed' draws all N targets; 'compact' draws the importance prefix only. */
  silhouetteConfiguration?: 'fixed' | 'compact';
}

export default function RexyOrbThree({
  size,
  userAmplitude,
  agentAmplitude,
  warmth,
  sessionActive,
  idleFormation = 'sphere',
  silhouetteConfiguration = 'fixed',
}: RexyOrbThreeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // WebGL availability — checked once per mount. If false we render the SVG fallback
  // and skip the entire Three.js path. `null` = not yet probed (first render guard).
  const [webglOk, setWebglOk] = useState<boolean | null>(null);

  // Live inputs mirrored into refs each render → rAF reads latest, no re-subscribe.
  const ampURef = useRef(userAmplitude);
  const ampARef = useRef(agentAmplitude);
  const warmthRef = useRef(warmth);
  const sessionRef = useRef(sessionActive);
  const idleFormationRef = useRef(idleFormation);
  const silhouetteConfigurationRef = useRef(silhouetteConfiguration);
  ampURef.current = userAmplitude;
  ampARef.current = agentAmplitude;
  warmthRef.current = warmth;
  sessionRef.current = sessionActive;
  idleFormationRef.current = idleFormation;
  silhouetteConfigurationRef.current = silhouetteConfiguration;

  const initial = useMemo(makeInitialState, []);

  // GPU objects shared across the mount effect + resize effect.
  const gl = useRef<{
    renderer: THREE.WebGLRenderer;
    pointsMat: THREE.ShaderMaterial;
  } | null>(null);

  // Probe WebGL once on mount, BEFORE the heavy renderer effect runs.
  useEffect(() => {
    const ok = probeWebGL();
    if (!ok) {
      // eslint-disable-next-line no-console
      console.warn(
        '[RexyOrbThree] WebGL not available — falling back to static orb. ' +
          'Particle animation disabled, app remains fully functional.',
      );
    }
    setWebglOk(ok);
  }, []);

  // ── Build scene + run the sim/render loop (mount once) ───────────────────────
  useEffect(() => {
    // Skip the entire Three.js setup if WebGL isn't usable or hasn't been probed yet.
    if (webglOk !== true) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ampDamp = reduceMotion ? 0.25 : 1.0;

    // Guarded renderer construction — if THREE still fails despite the probe
    // (drivers can go bad mid-session), fall back rather than crash the app.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[RexyOrbThree] WebGLRenderer construction failed, falling back:', err);
      setWebglOk(false);
      return;
    }
    renderer.setClearColor(0x000000, 0); // transparent — atmosphere shows through
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(pixelRatio);
    // P2 — set the drawing-buffer size on mount. The resize effect ([size]) runs
    // once before gl.current exists (webglOk gate) and skips setSize, so without this
    // the renderer kept its default 300x150 buffer (blurry) until a window-drag fired
    // the resize. Setting it here makes the orb sharp on first paint.
    renderer.setSize(size, size, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.z = CAM_Z;

    // Particle geometry — position + per-particle envelope alpha, both dynamic.
    const posArr = new Float32Array(N * 3);
    const alphaArr = new Float32Array(N);
    const geometry = new THREE.BufferGeometry();
    const posAttr = new THREE.BufferAttribute(posArr, 3).setUsage(THREE.DynamicDrawUsage);
    const alphaAttr = new THREE.BufferAttribute(alphaArr, 1).setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', posAttr);
    geometry.setAttribute('aAlpha', alphaAttr);

    const pointsMat = new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uPointBasePx: { value: size * 0.014 }, // D1: was 0.045 — far too large
        uPixelRatio: { value: pixelRatio },
        uCamZ: { value: CAM_Z },
        uStarCool: {
          value: new THREE.Vector3(STAR_COOL[0] / 255, STAR_COOL[1] / 255, STAR_COOL[2] / 255),
        },
        uStarGold: {
          value: new THREE.Vector3(STAR_GOLD[0] / 255, STAR_GOLD[1] / 255, STAR_GOLD[2] / 255),
        },
        uWarmth: { value: warmthRef.current },
        uDim: { value: IDLE_DIM },
      },
    });
    const points = new THREE.Points(geometry, pointsMat);
    points.frustumCulled = false;
    scene.add(points);

    // Core quads — one shared plane geometry; diffuse core behind, hot core in front.
    const coreGeo = new THREE.PlaneGeometry(1, 1);

    const diffuseMat = new THREE.ShaderMaterial({
      vertexShader: CORE_VERT,
      fragmentShader: CORE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      // Diffuse glow supports the particles, never washes them (D1: reduced opacity).
      uniforms: coreUniforms(CORE_COOL, CORE_WARM, CORE_POS, 0.6),
    });
    const diffuseCore = new THREE.Mesh(coreGeo, diffuseMat);
    diffuseCore.renderOrder = -1; // behind particles
    scene.add(diffuseCore);

    // Hot core: 3 stops at [0,0.35,1] padded to 5; per-stop aMul (0.98/0.46/0) baked in.
    const HOT_AMUL = [0.98, 0.46, 0.0];
    const hotCool: [number, number, number, number][] = [0, 1, 2, 2, 2].map((si, idx) => [
      CORE_COOL[si][0],
      CORE_COOL[si][1],
      CORE_COOL[si][2],
      idx < 3 ? CORE_COOL[si][3] * HOT_AMUL[idx] : 0,
    ]);
    const hotWarm: [number, number, number, number][] = [0, 1, 2, 2, 2].map((si, idx) => [
      CORE_WARM[si][0],
      CORE_WARM[si][1],
      CORE_WARM[si][2],
      idx < 3 ? CORE_WARM[si][3] * HOT_AMUL[idx] : 0,
    ]);
    const hotMat = new THREE.ShaderMaterial({
      vertexShader: CORE_VERT,
      fragmentShader: CORE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: coreUniforms(hotCool, hotWarm, [0, 0.35, 1, 1, 1], 1.0),
    });
    const hotCore = new THREE.Mesh(coreGeo, hotMat);
    hotCore.renderOrder = 1; // in front of particles
    scene.add(hotCore);

    // World half-extents. D1: diffuse trimmed from 1.5 (foggy under additive) to 1.0;
    // hot tightened from 0.2 to 0.065 so it reads as a bright emitter, not a blob.
    const DIFFUSE_HALF = 1.0;
    const HOT_HALF = 0.065;

    gl.current = { renderer, pointsMat };

    const state = initial; // mutated in place

    // A Rexy-idle instance is BORN in the silhouette. Seeding here rather than
    // in makeInitialState leaves that function untouched, and starting on the
    // targets means nothing renders a proto-morph from the sphere. Age and seed
    // keep their existing staggered values. Guarded on !session so a reload
    // DURING a call still mounts straight into the accepted orb.
    if (idleFormationRef.current === 'rexy' && !sessionRef.current) {
      for (let i = 0; i < SIL_N; i++) {
        const o = i * STRIDE;
        state[o] = REXY_SILHOUETTE_POSITIONS[i * 3];
        state[o + 1] = REXY_SILHOUETTE_POSITIONS[i * 3 + 1];
        state[o + 2] = REXY_SILHOUETTE_POSITIONS[i * 3 + 2];
        state[o + 3] = 0;
        state[o + 4] = 0;
        state[o + 5] = 0;
      }
    }
    let appliedDrawRange = -1;

    // ── S2 formation state machine ────────────────────────────────────────
    // Visual state only. sessionActive remains the SOLE truth about the call;
    // nothing here delays, intercepts or touches the call itself.
    const rexyMode = idleFormationRef.current === 'rexy';
    let formationPhase: RexyFormationPhase =
      !rexyMode || sessionRef.current ? 'orb-active' : 'silhouette-idle';
    let prevSession: boolean | null = null;

    // Allocated ONCE. Nothing below allocates per particle per frame.
    const transitionStart = new Float32Array(N * 3);
    const transitionControl = new Float32Array(N * 3);
    const transitionTarget = new Float32Array(N * 3);
    const sphereTargets = new Float32Array(N * 3);

    let transitionStartMs = 0;
    let transitionDurationMs = TO_ORB_DURATION_MS;
    let transitionDirection: 'to-orb' | 'to-silhouette' = 'to-orb';

    // The live core position, tracked so a transition can snapshot exactly
    // where the core is at the instant it starts (including mid-travel).
    const idleCore = rexyMode && !sessionRef.current;
    let coreX: number = idleCore ? REXY_EYE_ANCHOR[0] : 0;
    let coreY: number = idleCore ? REXY_EYE_ANCHOR[1] : 0;
    let coreZ: number = idleCore ? REXY_EYE_ANCHOR[2] : 0;
    let coreStartX: number = coreX;
    let coreStartY: number = coreY;
    let coreStartZ: number = coreZ;
    let coreEndX: number = coreX;
    let coreEndY: number = coreY;
    let coreEndZ: number = coreZ;

    // Additive phase offset so entering the orb never jumps to an arbitrary
    // global-time angle. ROT_SPEED itself is untouched.
    let orbRotationOffset = 0;
    let orbEntryStartedMs = -1;
    // Scaled clock for the active-orb noise + recycle inputs. Seeded from the
    // wall clock so the deterministic noise phase starts where it always did.
    let orbMotionTime = performance.now() / 1000;
    let lastFrameMs = performance.now();

    const beginFormationTransition = (
      direction: 'to-orb' | 'to-silhouette',
      nowMs: number,
      nowSeconds: number,
    ) => {
      // Leaving the live orb: bake the object rotation into the particles so
      // the return starts from what is actually on screen. Done ONCE, here.
      if (direction === 'to-silhouette' && formationPhase === 'orb-active') {
        const angle = points.rotation.y;
        const c = Math.cos(angle);
        const sn = Math.sin(angle);
        for (let i = 0; i < N; i++) {
          const o = i * STRIDE;
          const x = state[o];
          const z = state[o + 2];
          const vx = state[o + 3];
          const vz = state[o + 5];
          state[o] = c * x + sn * z;
          state[o + 2] = -sn * x + c * z;
          state[o + 3] = c * vx + sn * vz;
          state[o + 5] = -sn * vx + c * vz;
        }
        points.rotation.y = 0;
        orbRotationOffset = 0;
      }

      const scatterScale = reduceMotion ? REDUCED_SCATTER_SCALE : 1.0;

      for (let i = 0; i < N; i++) {
        const o = i * STRIDE;
        const b = i * 3;
        const seed = state[o + 7];

        const sx0 = state[o];
        const sy0 = state[o + 1];
        const sz0 = state[o + 2];
        transitionStart[b] = sx0;
        transitionStart[b + 1] = sy0;
        transitionStart[b + 2] = sz0;

        // Destination. to-orb uses the ACCEPTED recycle distribution so the
        // arrival set is exactly what the orb would have produced itself.
        let tx: number;
        let ty: number;
        let tz: number;
        if (direction === 'to-orb') {
          const theta = noise(seed, nowSeconds) * Math.PI;
          const phi = (noise(seed + 7, nowSeconds) * 0.5 + 0.5) * Math.PI;
          tx = Math.sin(phi) * Math.cos(theta);
          ty = Math.cos(phi);
          tz = Math.sin(phi) * Math.sin(theta);
          sphereTargets[b] = tx;
          sphereTargets[b + 1] = ty;
          sphereTargets[b + 2] = tz;
        } else {
          const k = i < SIL_N ? b : 0;
          tx = REXY_SILHOUETTE_POSITIONS[k];
          ty = REXY_SILHOUETTE_POSITIONS[k + 1];
          tz = REXY_SILHOUETTE_POSITIONS[k + 2];
        }
        transitionTarget[b] = tx;
        transitionTarget[b + 1] = ty;
        transitionTarget[b + 2] = tz;

        // Outward direction, measured from the core that is on screen now.
        let dx = sx0 - coreX;
        let dy = sy0 - coreY;
        let dz = sz0 - coreZ;
        let dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < 1e-4) {
          if (direction === 'to-orb' && i < SIL_N) {
            dx = REXY_SILHOUETTE_NORMALS[b];
            dy = REXY_SILHOUETTE_NORMALS[b + 1];
            dz = REXY_SILHOUETTE_NORMALS[b + 2];
          } else {
            dx = sx0;
            dy = sy0;
            dz = sz0;
          }
          dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        }
        const outX = dx / dist;
        const outY = dy / dist;
        const outZ = dz / dist;

        // Deterministic tangent perpendicular to `out`, rotated by the seed —
        // dispersion with a little curl, never Math.random in the loop.
        let ax = 0;
        let ay = 0;
        let az = 1;
        if (Math.abs(outZ) > 0.9) {
          ax = 1;
          az = 0;
        }
        let t1x = outY * az - outZ * ay;
        let t1y = outZ * ax - outX * az;
        let t1z = outX * ay - outY * ax;
        const t1n = Math.sqrt(t1x * t1x + t1y * t1y + t1z * t1z) || 1;
        t1x /= t1n;
        t1y /= t1n;
        t1z /= t1n;
        const t2x = outY * t1z - outZ * t1y;
        const t2y = outZ * t1x - outX * t1z;
        const t2z = outX * t1y - outY * t1x;
        const ang = hash01(seed + 72.1) * TAU;
        const ca = Math.cos(ang);
        const sa = Math.sin(ang);
        const curl = (hash01(seed + 19.3) - 0.5) * 2 * SCATTER_CURL * scatterScale;

        const scatter =
          (SCATTER_MIN + hash01(seed + 41.7) * (SCATTER_MAX - SCATTER_MIN)) * scatterScale;

        transitionControl[b] = sx0 + outX * scatter + (t1x * ca + t2x * sa) * curl;
        transitionControl[b + 1] = sy0 + outY * scatter + (t1y * ca + t2y * sa) * curl;
        transitionControl[b + 2] = sz0 + outZ * scatter + (t1z * ca + t2z * sa) * curl;
      }

      coreStartX = coreX;
      coreStartY = coreY;
      coreStartZ = coreZ;
      if (direction === 'to-orb') {
        coreEndX = 0;
        coreEndY = 0;
        coreEndZ = 0;
      } else {
        coreEndX = REXY_EYE_ANCHOR[0];
        coreEndY = REXY_EYE_ANCHOR[1];
        coreEndZ = REXY_EYE_ANCHOR[2];
      }

      transitionDirection = direction;
      transitionDurationMs = reduceMotion
        ? REDUCED_TRANSITION_DURATION_MS
        : direction === 'to-orb'
          ? TO_ORB_DURATION_MS
          : TO_SILHOUETTE_DURATION_MS;
      transitionStartMs = nowMs;
      formationPhase = direction;
    };

    // ── trace buffers: allocated ONCE, only when tracing is armed ──────────
    const tracing = traceEnabled();
    const traceBuf = tracing ? new Float64Array(TRACE_CAP * TRACE_FIELDS) : null;
    const tracePrevPos = tracing ? new Float32Array(N * 3) : null;
    const tracePrevVel = tracing ? new Float32Array(N * 3) : null;
    const tracePrevAge = tracing ? new Float32Array(N) : null;
    let traceCount = 0;
    let traceLastMs = 0;
    const longTasks: number[][] = [];
    let longTaskObs: PerformanceObserver | null = null;
    if (tracing && typeof PerformanceObserver !== 'undefined') {
      try {
        longTaskObs = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) longTasks.push([e.startTime, e.duration]);
        });
        longTaskObs.observe({ entryTypes: ['longtask'] });
      } catch {
        longTaskObs = null;
      }
    }
    if (tracing) {
      (window as any).__REXY_EXPORT_TRANSITION_TRACE__ = () => {
        const rows: number[][] = [];
        for (let r = 0; r < traceCount; r++) {
          const row: number[] = [];
          for (let f = 0; f < TRACE_FIELDS; f++) row.push(traceBuf![r * TRACE_FIELDS + f]);
          rows.push(row);
        }
        return {
          fields: [
            'tMs', 'frameDelta', 'stepMs', 'phase', 'dir', 'globalProgress',
            'minLocal', 'medLocal', 'maxLocal', 'handoffCount', 'coreProgress',
            'coreX', 'coreY', 'coreZ', 'rotY', 'rotOffset', 'dim', 'warmthCur',
            'ampSmooth', 'session', 'userAmp', 'agentAmp', 'recycled',
            'rmsPosDelta', 'maxPosDelta', 'rmsVelDelta', 'maxVelDelta', 'drawRange',
            'driftRms',
          ],
          rows,
          longTasks,
        };
      };
    }

    let ampSmooth = 0;
    // Start at the current target so the first mount doesn't fade in from black.
    let dim = sessionRef.current ? 1.0 : IDLE_DIM;
    // Eased warmth — starts AT the target so mount doesn't crossfade from cool.
    let warmthCur = warmthRef.current;
    let raf = 0;

    const step = () => {
      const stepT0 = tracing ? performance.now() : 0;
      const t = performance.now() / 1000;

      // Smoothed amplitude — fast attack, slow release (persists across frames).
      const rawAmp = Math.max(ampURef.current, ampARef.current) * ampDamp;
      ampSmooth += (rawAmp - ampSmooth) * (rawAmp > ampSmooth ? 0.12 : 0.04);
      const amp = ampSmooth;
      const reactProbability = 0.03 + amp * 0.07; // 3–10% of particles
      const impulse = 0.01 + amp * 0.02;

      // ── S1.2 / S2 — formation. Mutually exclusive with the orb sim below. ──
      // The accepted loop is guarded by a brace-less `if (runOrbSim)` so that
      // not one character of it — including its indentation — is disturbed.
      const nowMs = performance.now();

      // Session edges drive the transition. No hard cut remains.
      const session = sessionRef.current;
      if (rexyMode && prevSession !== null && session !== prevSession) {
        beginFormationTransition(session ? 'to-orb' : 'to-silhouette', nowMs, t);
      }
      prevSession = session;

      // drawRange follows the CONFIGURATION ONLY, never the call state or the
      // formation phase: a compact control stays 2048 visible particles as
      // silhouette, through the transition, AND as orb. Buffers stay allocated
      // at N; only the draw count moves, and only when it changes.
      const wantRange =
        silhouetteConfigurationRef.current === 'compact'
          ? Math.min(REXY_PRIMARY_COUNT, N)
          : N;
      if (wantRange !== appliedDrawRange) {
        geometry.setDrawRange(0, wantRange);
        appliedDrawRange = wantRange;
      }

      // ── Rexy idle: ONE Mexican-wave crest ─────────────────────────────────
      if (formationPhase === 'silhouette-idle') {
        const waveMotionScale = reduceMotion ? 0.25 : 1.0;
        const cycleSeconds = MEXICAN_WAVE_TRAVEL_SECONDS + MEXICAN_WAVE_REST_SECONDS;
        const cycleTime = t % cycleSeconds;
        const travelling = cycleTime < MEXICAN_WAVE_TRAVEL_SECONDS;
        const crestCoordinate = travelling
          ? -MEXICAN_WAVE_MARGIN +
            (cycleTime / MEXICAN_WAVE_TRAVEL_SECONDS) * (1 + MEXICAN_WAVE_MARGIN * 2)
          : 0;

        for (let i = 0; i < SIL_N; i++) {
          const o = i * STRIDE;
          const b = i * 3;

          // Lifecycle preserved; on recycle the particle keeps its own target
          // instead of respawning on a sphere, so nothing ever teleports.
          let age = state[o + 6] + 1;
          if (age >= TOTAL) age = 0;
          state[o + 6] = age;

          let crest = 0;
          if (travelling) {
            const nd = (REXY_WAVE_COORDINATES[i] - crestCoordinate) / MEXICAN_WAVE_HALF_WIDTH;
            if (nd > -1 && nd < 1) {
              const raisedCosine = 0.5 + 0.5 * Math.cos(Math.PI * nd);
              crest =
                Math.pow(raisedCosine, MEXICAN_WAVE_SHARPNESS) * MEXICAN_WAVE_AMPLITUDE;
            }
          }
          const r = crest * waveMotionScale;

          const targetX = REXY_SILHOUETTE_POSITIONS[b] + REXY_SILHOUETTE_NORMALS[b] * r;
          const targetY =
            REXY_SILHOUETTE_POSITIONS[b + 1] + REXY_SILHOUETTE_NORMALS[b + 1] * r;
          const targetZ =
            REXY_SILHOUETTE_POSITIONS[b + 2] + REXY_SILHOUETTE_NORMALS[b + 2] * r;

          // Soft formation spring. NO random accel, NO shell pull, NO audio
          // impulse here — those stay exclusively in the accepted orb branch.
          const vx = (state[o + 3] + (targetX - state[o]) * SILHOUETTE_SPRING) * SILHOUETTE_DAMPING;
          const vy =
            (state[o + 4] + (targetY - state[o + 1]) * SILHOUETTE_SPRING) * SILHOUETTE_DAMPING;
          const vz =
            (state[o + 5] + (targetZ - state[o + 2]) * SILHOUETTE_SPRING) * SILHOUETTE_DAMPING;
          state[o + 3] = vx;
          state[o + 4] = vy;
          state[o + 5] = vz;
          state[o] += vx;
          state[o + 1] += vy;
          state[o + 2] += vz;

          const a6 = state[o + 6];
          let env: number;
          if (a6 < ATTACK) env = a6 / ATTACK;
          else if (a6 < ATTACK + HOLD) env = 1;
          else env = 1 - (a6 - ATTACK - HOLD) / DECAY;
          env = env < 0 ? 0 : env > 1 ? 1 : env;

          posArr[b] = state[o];
          posArr[b + 1] = state[o + 1];
          posArr[b + 2] = state[o + 2];
          alphaArr[i] = env;
        }
      }

      // ── Transition: one staggered quadratic Bezier per particle ───────────
      if (formationPhase === 'to-orb' || formationPhase === 'to-silhouette') {
        const globalProgress = clamp01((nowMs - transitionStartMs) / transitionDurationMs);
        const maxStagger = reduceMotion ? REDUCED_MAX_STAGGER : TRANSITION_MAX_STAGGER;
        const span = 1 - maxStagger;
        const toOrb = transitionDirection === 'to-orb';

        for (let i = 0; i < N; i++) {
          const o = i * STRIDE;
          const b = i * 3;

          let age = state[o + 6] + 1;
          if (age >= TOTAL) age = 0; // never reseeds position: stays on its path
          state[o + 6] = age;

          const local = clamp01((globalProgress - RELEASE_DELAY_01[i] * maxStagger) / span);
          const eased = smootherstep(local);
          const om = 1 - eased;
          const w0 = om * om;
          const w1 = 2 * om * eased;
          const w2 = eased * eased;

          const nx = w0 * transitionStart[b] + w1 * transitionControl[b] + w2 * transitionTarget[b];
          const ny =
            w0 * transitionStart[b + 1] + w1 * transitionControl[b + 1] + w2 * transitionTarget[b + 1];
          const nz =
            w0 * transitionStart[b + 2] + w1 * transitionControl[b + 2] + w2 * transitionTarget[b + 2];

          let vx = nx - state[o];
          let vy = ny - state[o + 1];
          let vz = nz - state[o + 2];

          if (toOrb) {
            // S2.1 PART A — no early velocity override. Velocity stays the ACTUAL
            // rendered frame-to-frame displacement, so the particle arrives
            // carrying real motion instead of a synthesised value. Continuity is
            // now handled after arrival by the orb-entry force blend.
          } else {
            const settle = smoothstep(0.85, 1.0, local);
            vx *= 1 - settle;
            vy *= 1 - settle;
            vz *= 1 - settle;
          }

          state[o] = nx;
          state[o + 1] = ny;
          state[o + 2] = nz;
          state[o + 3] = vx;
          state[o + 4] = vy;
          state[o + 5] = vz;

          const a6 = state[o + 6];
          let env: number;
          if (a6 < ATTACK) env = a6 / ATTACK;
          else if (a6 < ATTACK + HOLD) env = 1;
          else env = 1 - (a6 - ATTACK - HOLD) / DECAY;
          env = env < 0 ? 0 : env > 1 ? 1 : env;

          posArr[b] = nx;
          posArr[b + 1] = ny;
          posArr[b + 2] = nz;
          alphaArr[i] = env;
        }

        if (globalProgress >= 1) {
          for (let i = 0; i < N; i++) {
            const o = i * STRIDE;
            const b = i * 3;
            state[o] = transitionTarget[b];
            state[o + 1] = transitionTarget[b + 1];
            state[o + 2] = transitionTarget[b + 2];
            if (toOrb) {
              // Velocity is whatever the path actually left it at — untouched.
            } else {
              state[o + 3] = 0;
              state[o + 4] = 0;
              state[o + 5] = 0;
            }
            posArr[b] = state[o];
            posArr[b + 1] = state[o + 1];
            posArr[b + 2] = state[o + 2];
          }
          if (toOrb) {
            // Normalise the entry angle to zero WITHOUT touching ROT_SPEED.
            orbRotationOffset = -t * ROT_SPEED; // assigned ONCE, on entry only
            points.rotation.y = 0;
            formationPhase = 'orb-active';
            orbEntryStartedMs = nowMs;
          } else {
            points.rotation.y = 0;
            orbRotationOffset = 0;
            formationPhase = 'silhouette-idle';
          }
        }
      }

      // S2.1 PART 1 — computed HERE, after the transition block above may have
      // flipped the phase this very frame. Reading it earlier left the
      // completion frame running neither integrator: a one-frame dead stop.
      const runOrbSim = !rexyMode || formationPhase === 'orb-active';

      const frameDeltaSeconds = Math.min(0.05, (nowMs - lastFrameMs) / 1000);
      lastFrameMs = nowMs;
      const motionScale = ORB_PARTICLE_TIME_SCALE;
      const motionDamping = Math.pow(DAMPING, motionScale);
      if (runOrbSim) orbMotionTime += frameDeltaSeconds * motionScale;
      const orbT = orbMotionTime;
      const orbEntryProgress =
        orbEntryStartedMs < 0
          ? 1
          : smootherstep(clamp01((nowMs - orbEntryStartedMs) / ORB_ENTRY_BLEND_MS));
      if (orbEntryStartedMs >= 0 && orbEntryProgress >= 1) orbEntryStartedMs = -1;

      if (runOrbSim)
      // ── Sim — 1:1 with iOS ────────────────────────────────────────────────
      for (let i = 0; i < N; i++) {
        const o = i * STRIDE;
        const seed = state[o + 7];
        const age = state[o + 6] + 1;

        if (age >= TOTAL) {
          // Recycle distribution UNCHANGED — only the clock it reads is the
          // pace-scaled one, so the whole system slows uniformly.
          const theta = noise(seed, orbT) * Math.PI;
          const phi = (noise(seed + 7, orbT) * 0.5 + 0.5) * Math.PI;
          const sx = Math.sin(phi) * Math.cos(theta);
          const sy = Math.cos(phi);
          const sz = Math.sin(phi) * Math.sin(theta);
          state[o] = sx;
          state[o + 1] = sy;
          state[o + 2] = sz;
          state[o + 3] = sx * 0.002;
          state[o + 4] = sy * 0.002;
          state[o + 5] = sz * 0.002;
          state[o + 6] = 0;
        } else {
          const stuckTime = 80 + hash01(seed + 3) * 20; // ~80–100 frames
          if (age > stuckTime) {
            const x = state[o];
            const y = state[o + 1];
            const z = state[o + 2];
            // PART 3 — identical formulas, identical constants. Every force is
            // multiplied by motionScale and DAMPING is raised to it, which is a
            // uniform time rescale rather than a re-tune. Character is preserved
            // exactly; only the rate changes.
            let vx = (state[o + 3] + noise(seed, orbT) * RANDOM_ACCEL * motionScale) * motionDamping;
            let vy = (state[o + 4] + noise(seed + 17, orbT) * RANDOM_ACCEL * motionScale) * motionDamping;
            let vz = (state[o + 5] + noise(seed + 31, orbT) * RANDOM_ACCEL * motionScale) * motionDamping;

            // Soft shell constraint — pull back toward the unit-radius surface.
            const dist = Math.sqrt(x * x + y * y + z * z) || 1e-4;
            const nx = x / dist;
            const ny = y / dist;
            const nz = z / dist;
            const pull = (1 - dist) * SHELL_STRENGTH * motionScale;
            vx += nx * pull;
            vy += ny * pull;
            vz += nz * pull;

            // Individual reaction — the chosen few pop OUTWARD along their normal.
            // Selection probability and impulse direction are untouched.
            const sel = noise(seed * 1.7 + 3.1, orbT * 2.5) * 0.5 + 0.5;
            if (sel < reactProbability) {
              vx += nx * impulse * motionScale;
              vy += ny * impulse * motionScale;
              vz += nz * impulse * motionScale;
            }

            // PART 1B — orb-entry force blend. For ORB_ENTRY_BLEND_MS after the
            // transition lands, the accepted candidate velocity assumes control
            // gradually instead of replacing the arrival velocity outright. At
            // progress 1 this is exactly the accepted simulation.
            if (orbEntryProgress < 1) {
              vx = lerp(state[o + 3], vx, orbEntryProgress);
              vy = lerp(state[o + 4], vy, orbEntryProgress);
              vz = lerp(state[o + 5], vz, orbEntryProgress);
            }

            state[o + 3] = vx;
            state[o + 4] = vy;
            state[o + 5] = vz;
            state[o] = x + vx;
            state[o + 1] = y + vy;
            state[o + 2] = z + vz;
          }
          state[o + 6] = age;
        }

        // Envelope alpha (attack/hold/decay).
        const a6 = state[o + 6];
        let env: number;
        if (a6 < ATTACK) env = a6 / ATTACK;
        else if (a6 < ATTACK + HOLD) env = 1;
        else env = 1 - (a6 - ATTACK - HOLD) / DECAY;
        env = env < 0 ? 0 : env > 1 ? 1 : env;

        posArr[i * 3] = state[o];
        posArr[i * 3 + 1] = state[o + 1];
        posArr[i * 3 + 2] = state[o + 2];
        alphaArr[i] = env;
      }
      posAttr.needsUpdate = true;
      alphaAttr.needsUpdate = true;

      // Calm Y rotation (never audio-driven); frozen under reduced motion.
      if (runOrbSim)
      points.rotation.y = reduceMotion ? 0 : t * ROT_SPEED;
      // Rotation EPOCH: the accepted line above is byte-unchanged and still
      // advances at exactly ROT_SPEED. The offset only normalises the angle the
      // orb is entered at, so arriving from a transition never jumps.
      if (runOrbSim && !reduceMotion) points.rotation.y += orbRotationOffset;
      // Neither the head nor a transition spins like the sphere.
      if (!runOrbSim) points.rotation.y = 0;

      // Core pulse — independent 2s cycle (1s in / 1s out).
      const corePulse = 0.5 + 0.5 * Math.sin(t * Math.PI);
      const glowMul = 0.75 + 0.25 * corePulse;
      // Ease warmth toward the AppShell target (no raw flips). ~400ms settle.
      warmthCur += (warmthRef.current - warmthCur) * WARMTH_LERP;
      const w = warmthCur;

      // Smooth idle↔active brightness lerp (~1s). Core is ALWAYS present (≥IDLE_DIM).
      const dimTarget = sessionRef.current ? 1.0 : IDLE_DIM;
      dim += (dimTarget - dim) * DIM_LERP;

      pointsMat.uniforms.uWarmth.value = w;
      pointsMat.uniforms.uDim.value = dim;

      diffuseMat.uniforms.uWarmth.value = w;
      diffuseMat.uniforms.uGlowMul.value = glowMul;
      diffuseMat.uniforms.uDim.value = dim;
      const dScale = 2 * DIFFUSE_HALF * (1 + 0.08 * corePulse);
      diffuseCore.scale.set(dScale, dScale, 1);

      hotMat.uniforms.uWarmth.value = w;
      hotMat.uniforms.uDim.value = dim;
      const hScale = 2 * HOT_HALF * (1 + 0.03 * corePulse);
      hotCore.scale.set(hScale, hScale, 1);

      // The core IS the eye — the same two meshes, moved. No new mesh, no
      // pupil, no iris, no sprite, no crossfade. Heartbeat, scales, gradient,
      // colour and opacity above are untouched; only .position changes, and it
      // TRAVELS: eye -> centre on the way in, centre -> eye on the way back.
      if (formationPhase === 'silhouette-idle') {
        coreX = REXY_EYE_ANCHOR[0];
        coreY = REXY_EYE_ANCHOR[1];
        coreZ = REXY_EYE_ANCHOR[2];
      } else if (formationPhase === 'orb-active') {
        coreX = 0;
        coreY = 0;
        coreZ = 0;
      } else {
        const gp = clamp01((nowMs - transitionStartMs) / transitionDurationMs);
        // Inbound: the pulse leads and is home before the last particles land.
        // Outbound: it waits a beat, then travels back to the eye.
        const coreProgress =
          transitionDirection === 'to-orb'
            ? smootherstep(clamp01(gp / 0.74))
            : smootherstep(clamp01((gp - 0.08) / 0.78));
        coreX = lerp(coreStartX, coreEndX, coreProgress);
        coreY = lerp(coreStartY, coreEndY, coreProgress);
        coreZ = lerp(coreStartZ, coreEndZ, coreProgress);
      }
      diffuseCore.position.set(coreX, coreY, coreZ);
      hotCore.position.set(coreX, coreY, coreZ);

      if (tracing && traceCount < TRACE_CAP) {
        // Everything below is computed OUTSIDE the accepted simulation blocks.
        let sp = 0;
        let mp = 0;
        let sv = 0;
        let mv = 0;
        let rec = 0;
        let sd = 0;
        let nd = 0;
        const vis = appliedDrawRange > 0 ? appliedDrawRange : N;
        for (let i = 0; i < vis; i++) {
          const b = i * 3;
          const o = i * STRIDE;
          const dx = posArr[b] - tracePrevPos![b];
          const dy = posArr[b + 1] - tracePrevPos![b + 1];
          const dz = posArr[b + 2] - tracePrevPos![b + 2];
          const d2 = dx * dx + dy * dy + dz * dz;
          sp += d2;
          // DRIFT only: exclude the accepted recycle teleports (a jump across
          // the sphere is up to 2.0 units and swamps any RMS it appears in).
          if (d2 < 0.25) { sd += d2; nd++; }
          if (d2 > mp) mp = d2;
          const ex = state[o + 3] - tracePrevVel![b];
          const ey = state[o + 4] - tracePrevVel![b + 1];
          const ez = state[o + 5] - tracePrevVel![b + 2];
          const e2 = ex * ex + ey * ey + ez * ez;
          sv += e2;
          if (e2 > mv) mv = e2;
          if (state[o + 6] < tracePrevAge![i]) rec++;
          tracePrevPos![b] = posArr[b];
          tracePrevPos![b + 1] = posArr[b + 1];
          tracePrevPos![b + 2] = posArr[b + 2];
          tracePrevVel![b] = state[o + 3];
          tracePrevVel![b + 1] = state[o + 4];
          tracePrevVel![b + 2] = state[o + 5];
          tracePrevAge![i] = state[o + 6];
        }
        const inTransition = formationPhase === 'to-orb' || formationPhase === 'to-silhouette';
        const gp = inTransition
          ? clamp01((nowMs - transitionStartMs) / transitionDurationMs) : -1;
        const msg = reduceMotion ? REDUCED_MAX_STAGGER : TRANSITION_MAX_STAGGER;
        const loc = (d01: number) => clamp01((gp - d01 * msg) / (1 - msg));
        let hc = 0;
        if (inTransition) {
          for (let i = 0; i < vis; i++) {
            const l = loc(RELEASE_DELAY_01[i]);
            if (l >= 0.82 && l < 1) hc++;
          }
        }
        const cp = inTransition
          ? (transitionDirection === 'to-orb'
              ? smootherstep(clamp01(gp / 0.74))
              : smootherstep(clamp01((gp - 0.08) / 0.78)))
          : -1;
        const r0 = traceCount * TRACE_FIELDS;
        traceBuf![r0] = nowMs;
        traceBuf![r0 + 1] = traceLastMs ? nowMs - traceLastMs : 0;
        traceBuf![r0 + 2] = performance.now() - stepT0;
        traceBuf![r0 + 3] = PHASE_CODE[formationPhase];
        traceBuf![r0 + 4] = transitionDirection === 'to-orb' ? 1 : 3;
        traceBuf![r0 + 5] = gp;
        traceBuf![r0 + 6] = inTransition ? loc(0) : -1;
        traceBuf![r0 + 7] = inTransition ? loc(0.5) : -1;
        traceBuf![r0 + 8] = inTransition ? loc(1) : -1;
        traceBuf![r0 + 9] = hc;
        traceBuf![r0 + 10] = cp;
        traceBuf![r0 + 11] = coreX;
        traceBuf![r0 + 12] = coreY;
        traceBuf![r0 + 13] = coreZ;
        traceBuf![r0 + 14] = points.rotation.y;
        traceBuf![r0 + 15] = orbRotationOffset;
        traceBuf![r0 + 16] = dim;
        traceBuf![r0 + 17] = warmthCur;
        traceBuf![r0 + 18] = ampSmooth;
        traceBuf![r0 + 19] = sessionRef.current ? 1 : 0;
        traceBuf![r0 + 20] = ampURef.current;
        traceBuf![r0 + 21] = ampARef.current;
        traceBuf![r0 + 22] = rec;
        traceBuf![r0 + 23] = Math.sqrt(sp / vis);
        traceBuf![r0 + 24] = Math.sqrt(mp);
        traceBuf![r0 + 25] = Math.sqrt(sv / vis);
        traceBuf![r0 + 26] = Math.sqrt(mv);
        traceBuf![r0 + 27] = vis;
        traceBuf![r0 + 28] = nd ? Math.sqrt(sd / nd) : 0;
        traceLastMs = nowMs;
        traceCount++;
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    // ── Full GPU teardown — no leaks ──────────────────────────────────────────
    return () => {
      cancelAnimationFrame(raf);
      if (longTaskObs) longTaskObs.disconnect();
      geometry.dispose();
      pointsMat.dispose();
      coreGeo.dispose();
      diffuseMat.dispose();
      hotMat.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      gl.current = null;
    };
    // initial is stable (useMemo []); the loop reads live values via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, webglOk]);

  // ── Resize — update renderer size + perspective point scale ──────────────────
  useEffect(() => {
    const g = gl.current;
    if (!g || size <= 0) return;
    g.renderer.setSize(size, size, false);
    g.pointsMat.uniforms.uPointBasePx.value = size * 0.014;
  }, [size]);

  if (size <= 0) return null;

  // ── Fallback render: static SVG orb when WebGL is unavailable ────────────────
  // Mimics the diffuse core gradient (teal→gold by warmth) plus a few bright pin
  // points so the homepage still has a recognisable centre and the user can sign
  // in. No animation; this is purely a "the app is alive" affordance.
  if (webglOk === false) {
    const warmthClamped = Math.max(0, Math.min(1, warmth));
    // Lerp the two anchor stops we use most heavily in the diffuse core.
    const lerpHex = (cool: [number, number, number], warm: [number, number, number]) => {
      const r = Math.round(cool[0] + (warm[0] - cool[0]) * warmthClamped);
      const g = Math.round(cool[1] + (warm[1] - cool[1]) * warmthClamped);
      const b = Math.round(cool[2] + (warm[2] - cool[2]) * warmthClamped);
      return `rgb(${r},${g},${b})`;
    };
    const innerCol = lerpHex([140, 245, 255], [255, 225, 100]);
    const midCol = lerpHex([0, 205, 255], [255, 185, 35]);
    const outerCol = lerpHex([0, 160, 255], [255, 140, 20]);
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        style={{ display: 'block', pointerEvents: 'none' }}
        aria-hidden
      >
        <defs>
          <radialGradient id="rexy-fallback-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgb(250,255,255)" stopOpacity="0.92" />
            <stop offset="15%" stopColor={innerCol} stopOpacity="0.52" />
            <stop offset="40%" stopColor={midCol} stopOpacity="0.22" />
            <stop offset="75%" stopColor={outerCol} stopOpacity="0.075" />
            <stop offset="100%" stopColor={outerCol} stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="50" fill="url(#rexy-fallback-core)" />
        {/* A few bright pins so the orb reads as alive, not flat. Static positions
            chosen to look like a sphere shell silhouette. */}
        {[
          [50, 18],
          [78, 35],
          [82, 60],
          [62, 82],
          [34, 80],
          [18, 58],
          [22, 32],
          [42, 50],
        ].map(([cx, cy], i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r="1.2"
            fill="rgb(250,255,255)"
            opacity={sessionActive ? 0.95 : 0.65}
          />
        ))}
      </svg>
    );
  }

  // ── Live render: canvas hosting the Three.js renderer ────────────────────────
  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, display: 'block', pointerEvents: 'none' }}
    />
  );
}
